import { ChecklistRtl, Edit, Payment } from "@mui/icons-material"
import { Button, CardActions, Divider, Typography } from "@mui/material"
import Card from "@mui/material/Card"
import CardContent from "@mui/material/CardContent"
import CardHeader from "@mui/material/CardHeader"
import { grey } from "@mui/material/colors"
import moment from "jalali-moment"
import { useRouter } from "next/router"
import { FC } from "react"
import { toRial } from "../../../../components/common/inputs/CurrencyTextField"
import DetailList, { DetailListProps } from "../../../../components/detailList/DetailList"
import { DEFAULT_GIFT_CREDIT_FLOW, DEFAULT_GIFT_CREDIT_INSTALMENT } from "../../../../constants/giftCredit"
import { EnumMemberGiftCreditRequestStatus } from "../../../../services/gift-credit/payment/types"
import { IGiftCredit } from "../../../../services/gift-credit/types"

interface GiftCreditCardProps {
  card: IGiftCredit
}

const GiftCreditCard: FC<GiftCreditCardProps> = ({ card }) => {
  const router = useRouter()

  const items: DetailListProps["items"] = [
    {
      title: "نوع اعتبار",
      value: card?.giftCreditTypeTitle
    },
    {
      title: "نام تسهیلات",
      value: card?.creditLayerName
    },
    {
      title: "نام سازمان",
      value: card?.organizationName
    },
    {
      title: "شماره کارت مشتری",
      value: card?.userCardNumber ?? "-"
    },
    {
      title: "میزان اعتبار",
      value: toRial(card?.creditAmount)
    },
    {
      title: "وضعیت",
      value: card?.statusName
    }
  ]

  if (card?.rejectDescription) {
    items.push({
      title: "علت رد شدن اعتبار",
      value: card?.rejectDescription
    })
  }

  const handleSelectCredit = (giftCreditRequestGuid: string) => {
    router.push(`${DEFAULT_GIFT_CREDIT_FLOW}/${giftCreditRequestGuid}`)
  }

  const handlePaymentInstalment = (giftCreditRequestGuid: string) => {
    router.push(`${DEFAULT_GIFT_CREDIT_INSTALMENT}/${giftCreditRequestGuid}`)
  }

  const handleCompleteUserInfo = (giftCreditRequestGuid: string) => {
    router.push(`${DEFAULT_GIFT_CREDIT_FLOW}/${giftCreditRequestGuid}`)
  }

  const invalidStatus = [EnumMemberGiftCreditRequestStatus.CHARGED, EnumMemberGiftCreditRequestStatus.WAIT_FOR_RECHARGE]

  const shouldShowActions =
    (!invalidStatus.includes(card?.status)) ||
    card?.dataEntryNeeded ||
    (card?.requiresPayback && card?.status === EnumMemberGiftCreditRequestStatus.CHARGED)

  return (
    <Card variant='outlined' sx={{ borderRadius: 2 }}>
      <CardHeader
        sx={{ bgcolor: grey[100] }}
        title={`${card?.creditLayerName} ${card?.organizationName}`}
        subheader={
          <Typography variant='body1' pt={1.5} fontStyle='italic'>
            {moment(card?.createDate).locale("fa").format("DD MMMM YYYY")}
          </Typography>
        }
      />
      <CardContent sx={{ p: 0 }}>
        <DetailList items={items} hasBorder={false} itemBorder={false} />
      </CardContent>
      {shouldShowActions && <Divider />}

      <CardActions sx={{ justifyContent: "center", p: 1.5 }}>
        {!invalidStatus.includes(card?.status) && (
          <Button
            size='medium'
            variant='outlined'
            startIcon={<ChecklistRtl />}
            sx={{ borderRadius: "8px" }}
            onClick={() => handleSelectCredit(card?.giftCreditRequestGuid)}
          >
            ادامه فرآیند
          </Button>
        )}

        {card?.dataEntryNeeded && (
          <Button
            size='medium'
            variant='contained'
            startIcon={<Edit />}
            sx={{ borderRadius: "8px" }}
            onClick={() => handleCompleteUserInfo(card?.giftCreditRequestGuid)}
          >
            تکمیل و امضای قرارداد
          </Button>
        )}
        {card?.requiresPayback && card?.status === EnumMemberGiftCreditRequestStatus.CHARGED && (
          <Button
            size='medium'
            variant='contained'
            startIcon={<Payment />}
            sx={{ borderRadius: "8px" }}
            onClick={() => handlePaymentInstalment(card?.giftCreditRequestGuid)}
          >
            بازپرداخت اعتبار
          </Button>
        )}
      </CardActions>
    </Card>
  )
}

export default GiftCreditCard
